/**
 * @fileoverview Upload service for handling file uploads and downloads in the telehealth application.
 * Supports both image files (with metadata removal) and 3D model files (.ply format).
 * Provides functionality for creating presigned URLs, uploading files to cloud storage,
 * and generating download links with timestamped names.
 */

import { MeetingJoinType } from "../types";
import { createCustomToken, CustomAuthoriserData, getUrl } from "./service-utils";

const getUploadURL = (
    authData: CustomAuthoriserData,
    type: "upload" | "download",
) => {

    return getUrl(`photos/${authData.meetingId}/${type}`, authData.clientId, authData.userId)
}

/**
 * Creates a presigned URL for uploading a file to cloud storage.
 * 
 * @param authData - UserId, MeetingID, JoinCode or AuthToken for authorisation optional clientId
 * @param photoName - The original name of the file to upload
 * @returns Promise containing the upload URL and processed photo name
 * @throws Error if the API request fails
 */
const createUploadUrl = async (
    authData: CustomAuthoriserData,
    joinType: MeetingJoinType,
    photoName: string,
): Promise<{
    url: string;
    uploadPhotoName: string
}> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Create Upload URl incomplete parameters")
        return {
            url: "",
            uploadPhotoName: "",
        }
    }

    const uploadApiUrl = getUploadURL(authData, "upload");
    const extension = photoName.split(".").pop()?.toLocaleLowerCase()

    let joinTypeString = "Web"
    if (joinType === MeetingJoinType.Headset || joinType === MeetingJoinType.Phone) {
        joinTypeString = joinType
    }

    const params = {
        extension,
        joinType: joinTypeString,
    }

    const response = await fetch(uploadApiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params)
    });

    if (!response.ok) {
        console.error("Error creating upload url", response);
        throw new Error(`Failed to create upload URL: ${response.status} ${response.statusText}`);
    }

    return response.json();
};

/**
 * Creates a download link for an uploaded file with a timestamped filename.
 * 
 * @param authData - UserId, MeetingID, JoinCode or AuthToken for authorisation optional clientId
 * @param patientId - The ID of the patient the file belongs to
 * @param fileName - The name of the file to download
 * @returns Promise containing the download URL
 * @throws Error if the API request fails
 */
const createDownloadLink = async (
    authData: CustomAuthoriserData,
    fileName: string,
): Promise<string> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Create Upload URl incomplete parameters")
        return ""
    }

    const downloadAPIUrl = getUploadURL(authData, "download")

    const body = {
        fileName,
        timeZone: "Australia/Adelaide",
    };

    const response = await fetch(downloadAPIUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        console.error("Error creating download link", response);
        throw new Error(`Failed to create download link: ${response.status} ${response.statusText}`);
    }

    const downloadURL = await response.json();
    return downloadURL.url;
};

/**
 * Uploads an image file after removing metadata by redrawing it on a canvas.
 * This process ensures privacy by stripping EXIF data and other metadata.
 * 
 * @param authData - UserId, MeetingID, JoinCode or AuthToken for authorisation optional clientId
 * @param file - The image file to upload
 * @returns Promise containing the processed upload filename
 * @throws Error if upload URL creation, image processing, or upload fails
 */
const uploadPhoto = async (
    authData: CustomAuthoriserData,
    joinType: MeetingJoinType,
    file: File,
): Promise<string> => {

    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const allowedTypes = ["image/jpeg", "image/png"];
    const allowedExtensions = [".jpg", ".jpeg", ".png"];
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    const hasValidType = allowedTypes.includes(fileType);
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`Image is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
    }

    if (!hasValidType && !hasValidExtension) {
        throw new Error("Only PNG and JPEG image files are allowed.");
    }

    // Get presigned URL for upload
    const createResponse = await createUploadUrl(authData, joinType, file.name);

    if (createResponse instanceof Error) {
        console.error("Error getting upload url", createResponse);
        throw new Error("Failed to get upload URL");
    }

    const { url, uploadPhotoName } = createResponse;
    const arrayBuffer = await file.arrayBuffer();

    // Create initial blob from file data
    const blobData = new Blob([new Uint8Array(arrayBuffer)], { type: 'image/jpeg' });

    let processedImageBlob: Blob | null = null;

    try {
        // Remove metadata by redrawing image on canvas
        processedImageBlob = await new Promise<Blob>((resolve, reject) => {
            const image = new Image();
            image.src = URL.createObjectURL(blobData);

            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = image.width;
                canvas.height = image.height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error("Failed to get canvas context"));
                    return;
                }

                // Draw image to canvas (removes metadata)
                ctx.drawImage(image, 0, 0, image.width, image.height);

                // Convert canvas to blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("Failed to convert canvas to blob"));
                    }
                }, 'image/jpeg', 1);
            };

            image.onerror = () => {
                reject(new Error("Failed to load image"));
            };
        });
    } catch (error) {
        console.error("Error processing image:", error);
        throw error as Error
    }

    if (!processedImageBlob) {
        throw new Error("Image processing resulted in null blob");
    }

    // Upload processed image to presigned URL
    const uploadResult = await fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'image/jpeg',
        },
        body: processedImageBlob
    });

    if (!uploadResult.ok) {
        console.error("Error uploading image", uploadResult);
        throw new Error(`Failed to upload image: ${uploadResult.status} ${uploadResult.statusText}`);
    }

    return uploadPhotoName;
};

/**
 * Uploads a 3D model file (.ply format) to cloud storage.
 * 
 * @param authData - UserId, MeetingID, JoinCode or AuthToken for authorisation optional clientId
 * @param model - The model file to upload (should be a File or Blob)
 * @returns Promise containing the processed upload filename
 * @throws Error if upload URL creation or upload fails
 */
const uploadModel = async (
    authData: CustomAuthoriserData,
    joinType: MeetingJoinType,
    model: File,
): Promise<string> => {

    const MAX_FILE_SIZE_MB = 20;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    if (model.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`Image is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
    }

    // Get presigned URL for model upload
    const createResponse = await createUploadUrl(authData, joinType, model.name);

    if (createResponse instanceof Error) {
        console.error("Error getting upload url", createResponse);
        throw new Error("Failed to get upload URL for model");
    }

    const { url, uploadPhotoName } = createResponse;
    const arrayBuffer = await model.arrayBuffer();

    // Upload model data to presigned URL
    const uploadResult = await fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/ply',
        },
        body: arrayBuffer
    });

    if (!uploadResult.ok) {
        console.error("Error uploading model", uploadResult);
        throw new Error(`Failed to upload model: ${uploadResult.status} ${uploadResult.statusText}`);
    }

    return uploadPhotoName;
};

/**
 * Uploads a file (image or 3D model) and immediately creates a download URL for it.
 * Automatically detects file type based on extension and uses appropriate upload method.
 * 
 * @param file - The file to upload (image or .ply model)
 * @param authData - UserId, MeetingID, JoinCode or AuthToken for authorisation optional clientId
 * @param patientId - The ID of the patient the file belongs to
 * @returns Promise containing the download URL for the uploaded file
 * @throws Error if upload or download URL creation fails
 */
export const uploadFileAndCreateDownloadURL = async (
    file: File,
    joinType: MeetingJoinType,
    authData: CustomAuthoriserData,
): Promise<string> => {
    if (!file) {
        throw new Error("No file to upload")
    }

    // Determine file type and use appropriate upload method
    const isModel = file.name.endsWith(".ply");

    const uploadedFileName = isModel ?
        await uploadModel(authData, joinType, file) :
        await uploadPhoto(authData, joinType, file);

    // Create and return download URL
    return await createDownloadLink(authData, uploadedFileName);
};

/**
 * Downloads multiple images from their URLs by triggering browser downloads.
 * 
 * @param downloadURLs - Array of URLs to download
 */
export const downloadImages = async (downloadURLs: string[]): Promise<void> => {
    downloadURLs.forEach((downloadURL) => {
        downloadImage(downloadURL);
    });
};

/**
 * Downloads a single image from a URL by creating a canvas element and triggering a download.
 * Uses CORS-enabled image loading to handle cross-origin resources.
 * 
 * @param downloadURL - The URL of the image to download
 * 
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image#Implementing_the_save_feature}
 */
export const downloadImage = async (downloadURL: string): Promise<void> => {
    const img = new Image();

    // Enable cross-origin access for downloading image data
    img.crossOrigin = 'anonymous';
    img.src = downloadURL;

    img.onload = () => {
        // Create canvas to convert image to downloadable format
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            console.error("Failed to get canvas context for image download");
            return;
        }

        // Set canvas dimensions to match image
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image onto canvas
        ctx.drawImage(img, 0, 0);

        // Create download link element
        const downloadLink = document.createElement('a');
        const currentDate = new Date();

        downloadLink.download = `${currentDate.toDateString()}.png`;
        downloadLink.href = canvas.toDataURL('image/png');

        // Trigger download
        downloadLink.click();
    };

    img.onerror = () => {
        console.error("Failed to load image for download:", downloadURL);
    };
};